import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment";
import React, { useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { z } from "zod";
import { EndPoints } from "../../Constants/endpoints";
import { useToast } from "../../Services/ToastContext";
import profile from "../../assets/images/icons/profile.svg";
import warning from "../../assets/images/icons/warning.svg";
import { PatientDetails } from "../../interface/GeneralResponse";
import { RootState } from "../../redux/store";
import { useAppSelector, useFilter, usePostData } from "../../hooks";
import {
  DateInput,
  GenderRadioSelect,
  GeneralButton,
  GeneralInput,
  PhoneAutocompleteInput,
  ProfileAvatar,
} from "../../components";
import { useQueryClient } from "@tanstack/react-query";
import { truncateText } from "../../utils/stringsUtils";
import { useNavigate } from "react-router-dom";

interface PatientDetailsFormProps {
  onPrevious?: () => void;
  onSubmit: (data: PatientDetails) => void;
  withButtons?: boolean;
  OnlyMine?: boolean;
  prescriptionId?: any;
}

const PatientDetailsForm: React.FC<PatientDetailsFormProps> = ({
  onPrevious,
  onSubmit: onSubmitProp,
  withButtons = true,
  OnlyMine = false,
  prescriptionId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const validate = useSelector((state: RootState) => state.phValidation);
  const [selectedPatient, setSelectedPatient] = useState<PatientDetails>();
  const [hidePatientInfoInputs, setHidePatientInfoInputs] =
    useState<boolean>(true);
  const patientDetailsSchema = z.object({
    PhoneNumber: z
      .string()
      .min(9, { message: `${t("phonenumber_length_validation")} 9` })
      .superRefine((value, ctx) => {
        if (!validate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("validation_configuration_error"),
          });
          return;
        }

        const phoneNumber = value.replace(/[^0-9]/g, "");
        const countryStart = validate.Start.toString();
        const countryLength = validate.Length;

        const startWith0 = `0${countryStart}`;
        const startWithCountryStart = `${countryStart}`;

        if (phoneNumber.startsWith(startWith0)) {
          if (phoneNumber.length !== countryLength + 1) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("phone_number_must_be_and_start_with", {
                length: countryLength + 1,
                start: startWith0,
              }),
            });
          }
        } else if (phoneNumber.startsWith(startWithCountryStart)) {
          if (phoneNumber.length !== countryLength) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("phone_number_must_be_and_start_with", {
                length: countryLength,
                start: startWithCountryStart,
              }),
            });
          }
        } else {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("phone_number_must_start_with", {
              start1: startWithCountryStart,
              start2: startWith0,
            }),
          });
        }
      }),
    FullName: z.string().min(4, { message: `${t("fullname_min_length_4")}` }),
    Birthday: z.string().min(1, { message: t("birth_date_required") }),
    Gender: z.string().min(1, { message: t("please_select_your_gender") }),
  });

  type PatientDetailsFormData = z.infer<typeof patientDetailsSchema>;

  const methods = useForm<PatientDetailsFormData>({
    resolver: zodResolver(patientDetailsSchema),
    defaultValues: {
      PhoneNumber: "",
      FullName: "",
      Birthday: "",
      Gender: "",
    },
  });
  const { searchParams } = useFilter();
  const { control, handleSubmit, watch, reset } = methods;
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const phoneValue = watch("PhoneNumber");

  const { mutate: createPatient, isPending: isCreating } = usePostData(
    EndPoints.CreatePatient,
  );
  const { mutate: linkPatient, isPending: isLinking } = usePostData(
    EndPoints.LinkPatient,
  );

  const registration = useAppSelector((state: RootState) => state.registration);
  const countryId = Number(registration.userDetails.CountryId);

  const isMasked = selectedPatient?.FullName?.includes("*");

  const onOptionSelectPatient = (option: PatientDetails) => {
    setSelectedPatient({ ...option });

    reset({
      PhoneNumber: option.PhoneNumber || "",
      FullName: option.FullName || "",
      Birthday:
        option.Birthday === null || option.Birthday === undefined
          ? ""
          : option.Birthday,
      Gender: option?.Gender
        ? option.Gender.charAt(0).toLocaleUpperCase() +
          option.Gender.slice(1).toLocaleLowerCase()
        : "",
    });
  };

  const handleClearSelection = () => {
    reset({
      PhoneNumber: phoneValue || "",
      FullName: "",
      Birthday: "",
      Gender: "",
    });
    setSelectedPatient(undefined);
  };

  // Check if we need to show overlay (only when ALL fields are filled)
  const shouldShowOverlay =
    hidePatientInfoInputs &&
    selectedPatient?.Birthday !== null &&
    selectedPatient?.Birthday !== undefined &&
    selectedPatient?.Birthday !== "" &&
    selectedPatient?.Gender !== null &&
    selectedPatient?.Gender !== undefined &&
    selectedPatient?.Gender !== "";

  // Check if we're in link mode with missing fields
  const isLinkModeWithMissingFields =
    !!selectedPatient &&
    (!selectedPatient?.Birthday || !selectedPatient?.Gender);

  const isPatientComplete = (patient: PatientDetails) => {
    return (
      patient?.FullName &&
      patient?.PhoneNumber &&
      patient?.Birthday &&
      patient?.Gender
    );
  };
  const onSubmit: SubmitHandler<PatientDetailsFormData> = (data) => {
    if (selectedPatient) {
      if (
        isPatientComplete(selectedPatient) &&
        selectedPatient.IsLinked !== false
      ) {
        // Patient has all fields - navigate directly to prescription
        onSubmitProp(selectedPatient);
        // navigate(
        //   prescriptionId
        //     ? `/add_prescription_to_patient/${selectedPatient.Id}/${prescriptionId}`
        //     : `/add_prescription_to_patient/${selectedPatient.Id}`,
        //   { state: { isItComeFromMyPrescription: true } },
        // );
      } else {
        const linkData = {
          Id: selectedPatient.Id,
          Gender: data.Gender || selectedPatient.Gender,
          Birthday: data.Birthday || selectedPatient.Birthday,
        };

        linkPatient(linkData, {
          onSuccess: (response: any) => {
            showToast(t("patient_linked_successfully"), "success");
            const updatedPatient = {
              ...selectedPatient,
              ...linkData,
            };
            onSubmitProp(updatedPatient);
            // Navigate to prescription after successful link
            navigate(
              prescriptionId
                ? `/add_prescription_to_patient/${selectedPatient.Id}/${prescriptionId}`
                : `/add_prescription_to_patient/${selectedPatient.Id}`,
              { state: { isItComeFromMyPrescription: true } },
            );
            queryClient.refetchQueries({
              queryKey: [EndPoints.GetMyPatient],
              exact: false,
            });
            queryClient.refetchQueries({
              queryKey: [EndPoints.SearchMyPatients],
              exact: false,
            });
            queryClient.refetchQueries({
              queryKey: [EndPoints.SearchMyPatients],
              exact: false,
            });
          },
          onError: (error: any) => {
            showToast(error.message || t("failed_to_link_patient"), "error");
          },
        });
      }
    } else {
      // Create new patient
      const requestData = {
        FullName: data.FullName,
        PhoneNumber: data.PhoneNumber,
        Birthday: data.Birthday,
        Gender: data.Gender,
        CountryId: countryId,
      };

      createPatient(requestData, {
        onSuccess: (response: any) => {
          showToast(t("patient_created_successfully"), "success");
          onSubmitProp(response.Data);
          // Navigate to prescription with new patient
          navigate(`/add_prescription_to_patient/${response.Data.Id}`, {
            state: { isItComeFromMyPrescription: true },
          });
          queryClient.refetchQueries({
            queryKey: [EndPoints.GetMyPatient],
            exact: false,
          });
          queryClient.refetchQueries({
            queryKey: [EndPoints.SearchMyPatients],
            exact: false,
          });
        },
        onError: (error: any) => {
          showToast(error.message || t("failed_to_create_patient"), "error");
        },
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        id="patient-details-form"
        onSubmit={handleSubmit(onSubmit)}
        className="flex w-full flex-col gap-4"
      >
        {isMasked && (
          <div className="flex items-center gap-4 rounded-lg border border-orange bg-warning-50 p-2 text-sm font-bold text-orange">
            <img src={warning} alt="" />
            <p>{t("this_user_is_already_have_an_account_on_dome_care")}</p>
          </div>
        )}

        <PhoneAutocompleteInput<{ Data: PatientDetails[] }, PatientDetails>
          name="PhoneNumber"
          controllerName="PhoneNumber"
          control={control}
          getEndpoint={EndPoints.SearchMyPatients}
          onOptionSelect={(option: PatientDetails) =>
            onOptionSelectPatient(option)
          }
          onSearchResults={(options) => {
            if (!options || options?.length == 0) {
              handleClearSelection();
            }
            setHidePatientInfoInputs(options?.length > 0);
          }}
          getOptionValue={(o) => o.Id.toString()}
          getOptionLabel={(o) => ({
            value: o.PhoneNumber,
            label: (
              <div className="flex items-center gap-4">
                {o.FullName.includes("*") ? (
                  <img src={profile} alt="" />
                ) : (
                  <ProfileAvatar
                    Id={o.Id}
                    FullName={o.FullName}
                    src={o.Image}
                    size="small"
                    className="h-10 w-10 rounded-full"
                  />
                )}
                <span>{truncateText(o.FullName, 30)}</span>
              </div>
            ),
          })}
        />

        <div className="relative flex flex-col gap-4">
          {shouldShowOverlay && (
            <div className="absolute inset-0 z-10 bg-white/20" />
          )}

          <GeneralInput
            name="FullName"
            label={t("full_name")}
            placeholder={t("full_name")}
            charactersOnly
            maxLength={25}
            required
            disabled={!!selectedPatient}
          />

          <DateInput
            name="Birthday"
            label={t("birth_date")}
            dateConstraint="past"
            required={!selectedPatient || isLinkModeWithMissingFields}
            hideErrorMessage
            disabled={
              !!selectedPatient?.Birthday && !isLinkModeWithMissingFields
            }
          />
          {methods.formState.errors.Birthday && (
            <p className="text-xs text-error">{t("required_field")}</p>
          )}

          <GenderRadioSelect
            name="Gender"
            aria-describedby="gender-description"
            required={!selectedPatient || isLinkModeWithMissingFields}
            disabled={!!selectedPatient?.Gender && !isLinkModeWithMissingFields}
          />
        </div>

        {withButtons && (
          <div className="flex w-full justify-end gap-3">
            {onPrevious && (
              <GeneralButton
                type="button"
                title={t("previous")}
                color="lite"
                onClick={onPrevious}
              />
            )}
            <GeneralButton
              type="submit"
              title={selectedPatient ? t("link_to_patient") : t("next")}
              color="blue"
              isLoading={isCreating || isLinking}
            />
          </div>
        )}
      </form>
    </FormProvider>
  );
};

export default PatientDetailsForm;
